import {Nutritions} from "@/models/Recipes/Nutritions";
import {NutrientType} from "@/models/Ingredient/NutrientType";
import store from "@/store/store";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";

/**
 * Delays the execution of the provided callback function until after a specified time
 * has elapsed since the last invocation, ensuring that the callback is only called once
 * after the debounce period. It uses localStorage to store and manage the timeout
 * across further calls using a unique key.
 *
 * @param {Function} callback - The function to be executed after the debounce delay.
 * @param {number} [time=250] - The debounce delay in milliseconds, defaults to 250 ms if not specified.
 * @param {string} key - A unique key to manage the timeout in localStorage.
 * @return {void}
 */
export function debounce(callback: any, time: number = 250, key: string): void {
  let intervalId: any = localStorage.getItem(key);

  if (intervalId) {
    clearTimeout(intervalId);
  }

  intervalId = setTimeout(() => {
    callback();
  }, time);

  localStorage.setItem(key, intervalId);
}

export function getBoldString(mainString: string, subString: string) {
  if(subString == ""){
    return  `<b>${mainString}</b>`;
  }
  else{
    const index = mainString.toLowerCase().indexOf(subString.toLowerCase());
    if (index !== -1) {
      const startIndex = mainString.substring(0, index);
      const endIndex = mainString.substring(index + subString.length);
      const highlightedString = startIndex + "<b>" + mainString.substring(index, index + subString.length) + "</b>" + endIndex;
      return highlightedString;
    }
    return mainString;
  }
}


export function round(number: number, decimalPlaces: number) {
  return Math.round(number * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
}

export function shorten(text: string) {

  let newText = text.replace(',', '');
  newText = newText.replace('.', '');

  let parts = newText.split(' ');
  parts = parts.map((part) => {
    if (part.length > 3)
      return part.substr(0, 3) + '.';
    else {
      return part;
    }
  });

  return parts.join(' ');
}

export function pascalCaseToHuman(text: string) {
  return text.replace(/([a-z])(?=[A-Z])/g,"$1 ");
}

export function enumKeyByValue(enumType: object, value: any) {
  return Object.keys(enumType)[Object.values(enumType).indexOf(value)];
}

export function toCamelCase(text: string) {

  let parts = text.split(' ');
  for (let i = 0; i < parts.length; i++) {

    parts[i] = parts[i].charAt(0).toUpperCase() + parts[i].slice(1);
  }

  let cammeled = parts.join('');
  cammeled = cammeled.replace(',', '');

  return cammeled;
}

export function desanitizeString(html: string) {
  let txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

export function getErrorMessage(error: any, translations: any) {
  return  error.response &&
    error.response.data &&
    error.response.data.errorMessages &&
    error.response.data.errorMessages[0] &&
    error.response.data.errorMessages[0].userVisibleMessage
    ? error.response.data.errorMessages[0].userVisibleMessage
    : translations.get("toastr_network_error");
}

// Following functions are used to simplify the object nutritionAnalysis returned from backend

export function mapNutritions(nutritions: any | null): Nutritions {
  if (nutritions == null) {
    return new Nutritions(
      [],
      "g",
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      {
        item1: 0,
        item2: "mL"
      },
      {
        item1: 0,
        item2: "g"
      },
      null,
      null,
      LabelTypeMetadata.NutritionFacts,
      {},
      100,
      1,
      1,
      0,
    );
  }

  return new Nutritions(
    nutritions.totalNutrientsPerServing.map((nutrient: any) => ({
      nutrientName: nutrient.nutrientName,
      exactValue: nutrient.exactValue,
      exactDailyValue: nutrient.exactDailyValue,
      roundedValue: nutrient.roundedValue,
      roundedDailyValue: nutrient.roundedDailyValue,
      nutrientType: nutrient.nutrientType,
      originalNutrientAnalysis: nutrient.originalNutrientAnalysis,
    })),
    nutritions.servingUnitOfMeasurement,
    nutritions.weight,
    nutritions.weightPerServing,
    nutritions.roundedWeightPerServing,
    nutritions.rawWeight,
    nutritions.rawWeightPerServing,
    nutritions.volume,
    nutritions.rawVolume,
    nutritions.volumePerServing,
    nutritions.rawVolumePerServing,
    {
      item1: nutritions.quantityPerServingInUnitOfMeasurement.item1,
      item2: nutritions.quantityPerServingInUnitOfMeasurement.item2
    },
    {
      item1: nutritions.displayServingQuantityAndMeasure.item1,
      item2: nutritions.displayServingQuantityAndMeasure.item2
    },
    nutritions.servingQuantityInUnitOfMeasurementOverride,
    nutritions.servingUnitOfMeasurementOverride,
    nutritions.labelType,
    nutritions.additionalLabelInformation,
    nutritions.moistureYieldFactor,
    nutritions.ingredientScaleMultiplier,
    nutritions.servings,
    nutritions.originalMoistureInRecipe
  );
}

export function groupBy<K, V>(items: K[], key: (item: K) => V): Map<V, K[]> {
  return items.reduce((acc, item) => {
    const group = key(item);
    const values = acc.get(group) || [];
    values.push(item);
    acc.set(group, values);
    return acc;
  }, new Map<V, K[]>());
}

/**
 * Ensures the input value remains within the specified minimum and maximum range defined
 * by the input element's `min` and `max` attributes. If the new input value exceeds this range,
 * it clamps the value to the nearest bound and prevents the invalid input.
 *
 * @param {InputEvent} event - The input event triggered by the user typing or modifying the input value.
 * @return {void} Does not return a value. Modifies the input value directly if necessary.
 */
export function clampInputToMinMax(event: InputEvent): void {
  const input = event.target as HTMLInputElement;

  // Read min and max attributes
  const min = parseFloat(input.min);
  const max = parseFloat(input.max);

  const currentValue = input.value;
  const inputValue = event.data;

  const selectionStart = input.selectionStart;
  const selectionEnd = input.selectionEnd;

  const newValue = (
    currentValue.slice(0, selectionStart)
    + inputValue
    + currentValue.slice(selectionEnd)
  );

  // If the new value is not a number, ignore (but allow backspace, etc.)
  const numericValue = parseFloat(newValue);

  // Clamp the value
  if (numericValue < min) {
    event.preventDefault();
    input.value = min.toString();
  }

  else if (numericValue > max) {
    event.preventDefault();
    input.value = max.toString();
  }
}

/**
 * Intercepts the Enter key press event and prevents the default action from occurring.
 * @param event
 */
export function interceptEnterKey(event: KeyboardEvent) {
  console.log('Intercept fired');
  if (event.key === "Enter") {
    event.preventDefault();
  }
}

interface GenerableObject {
  [key: string]: any
}

interface vTableMetadata {
  id: string | number;
  link?: string;
}

/**
 * An HTML BeforeInput event handler designed to reject characters that would make the value fail the given regex.
 * Note that the pattern is expected to represent the COMPLETE string, not a per-character analysis.
 * @param pattern The RegExp pattern the `value` should conform to
 * @param $event The InputEvent fired from BeforeInput
 */
export function restrictCharactersByRegex(pattern: RegExp, $event: InputEvent) {
  const targetElement: HTMLInputElement = $event.target as HTMLInputElement;

  const currentValue = targetElement.value;
  const selectionStart = targetElement.selectionStart;
  const selectionEnd = targetElement.selectionEnd;

  const insertedText = $event.data || ''; // '' for deletions
  const predictedValue = (
    currentValue.slice(0, selectionStart)
    + insertedText
    + currentValue.slice(selectionEnd)
  );

  if (!pattern.test(predictedValue)) {
    // Reject change
    $event.preventDefault();
  }
}

export function buildVTableDataStruct(data: any[], meta: vTableMetadata & GenerableObject) {
  let rv: GenerableObject = {};

  data.forEach((item: any, index: number) => {
    // Index must be offset by 1 for vtable to correctly input the data
    rv[`e${index + 1}`] = item;
  });

  rv['meta'] = meta;
  return rv;
}

/**
 * Masks nutrient data if the user is on a trial
 * @param value The value to display or be masked
 * @param shouldRound whether or not the value should be rounded to 2 decimal places
 */
export function trialMaskedNutrientData(value: number, shouldRound: boolean = true) {
  if (store.getters.getUserDetails.isTrial) {
    return "--";
  }

  return shouldRound ? round(value, 2) : value;
}

export function tableMapper(item: { id: number, name: string, nutritionAnalysis: Nutritions }, useLink: boolean = false) {
  let recipeWeight = null;
  if (
    item.nutritionAnalysis.servingUnitOfMeasurement =="ml"
    || item.nutritionAnalysis.servingUnitOfMeasurement == "fl. oz"
  ) {
    recipeWeight = item.nutritionAnalysis.volume;
  }

  else {
    recipeWeight = item.nutritionAnalysis.weight;
  }

  const metaObj: vTableMetadata = {
    id: item.id
  };

  if (useLink) {
    metaObj.link = item.name;
  }

  const roundValue = (value: number) => round(value, 2);
  const getNutrientRoundedValue = (nutrient: NutrientType) => item.nutritionAnalysis.getRoundedValue(nutrient);

  return buildVTableDataStruct([
    trialMaskedNutrientData(getNutrientRoundedValue(NutrientType.Energy)),
    roundValue(getNutrientRoundedValue(NutrientType.Fat) * 9),
    roundValue(getNutrientRoundedValue(NutrientType.Fat)),
    roundValue(getNutrientRoundedValue(NutrientType.SaturatedFattyAcids)),
    roundValue(getNutrientRoundedValue(NutrientType.TransFattyAcids)),
    roundValue(getNutrientRoundedValue(NutrientType.Cholesterol)),
    roundValue(getNutrientRoundedValue(NutrientType.Sodium)),
    roundValue(getNutrientRoundedValue(NutrientType.Carbohydrate)),
    roundValue(getNutrientRoundedValue(NutrientType.Fibre)),
    roundValue(getNutrientRoundedValue(NutrientType.Sugars)),
    roundValue(getNutrientRoundedValue(NutrientType.Protein)),
    roundValue(getNutrientRoundedValue(NutrientType.VitaminD)),
    roundValue(getNutrientRoundedValue(NutrientType.VitaminC)),
    roundValue(getNutrientRoundedValue(NutrientType.Potassium)),
    roundValue(getNutrientRoundedValue(NutrientType.Calcium)),
    roundValue(getNutrientRoundedValue(NutrientType.Iron)),

    recipeWeight == null ? 0 : roundValue(recipeWeight)
  ],
  metaObj
  );
}

export async function downloadURI(uri: string, name: string) {
  try {
    const response = await fetch(uri, { credentials: "include" });

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    const blob = await response.blob();
    const link = document.createElement("a");
    link.download = name;
    link.href = URL.createObjectURL(blob);
    link.click();

    link.remove();
    URL.revokeObjectURL(link.href);
  }

  catch (error) {
    console.error("Download failed:", error);
  }
}

export function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

/**
 * Parses an ISO 8601 formatted string and converts it into a JavaScript Date object.
 *
 * @param {string} s - The ISO 8601 formatted string to be parsed.
 * @return {Date} A Date object representing the parsed date and time in UTC.
 */
export function parseISOString(s: string): Date {
  const b = s.split(/\D+/);
  // @ts-ignore
  return new Date(Date.UTC(b[0], --b[1], b[2], b[3], b[4], b[5], b[6]));
}

export function millisToSeconds(millis: number): number {
  return millis / 1000;
}

export function secondsToMinutes(seconds: number): number {
  return seconds / 60;
}

export function minutesToHours(minutes: number): number {
  return minutes / 60;
}

export function hoursToDays(hours: number): number {
  return hours / 24;
}

export function millisToDays(millis: number): number {
  return hoursToDays(minutesToHours(secondsToMinutes(millisToSeconds(millis))));
}

export const CANADA_PREVIEW_NUTRIENTS: NutrientType[] = [
  NutrientType.Energy,
  NutrientType.Fat,
  NutrientType.SaturatedFattyAcids,
  NutrientType.TransFattyAcids,
  NutrientType.Cholesterol,
  NutrientType.Carbohydrate,
  NutrientType.Fibre,
  NutrientType.Sugars,
  NutrientType.Protein,
  NutrientType.Sodium,
  NutrientType.Potassium,
  NutrientType.Calcium,
  NutrientType.Iron,
  NutrientType.VitaminD,
  NutrientType.Moisture
];

export const USA_PREVIEW_NUTRIENTS: NutrientType[] = [
  NutrientType.Energy,
  NutrientType.Fat,
  NutrientType.SaturatedFattyAcids,
  NutrientType.TransFattyAcids,
  NutrientType.Cholesterol,
  NutrientType.Carbohydrate,
  NutrientType.Fibre,
  NutrientType.Sugars,
  NutrientType.AddedSugar,
  NutrientType.Protein,
  NutrientType.Sodium,
  NutrientType.Potassium,
  NutrientType.Calcium,
  NutrientType.Iron,
  NutrientType.VitaminD,
  NutrientType.Moisture
];

export const UK_PREVIEW_NUTRIENTS: NutrientType[] = CANADA_PREVIEW_NUTRIENTS;

/**
 * Retrieves the preview nutrients list for a specified country.
 *
 * @param {NutritionLabelCountry} country - The country for which to retrieve the preview nutrients list.
 * @return {Array<string>} An array of nutrient names specific to the given country.
 */
export function getPreviewNutrientsForCountry(country: NutritionLabelCountry): NutrientType[] {
  switch (country) {
  case NutritionLabelCountry.Canada:
    return CANADA_PREVIEW_NUTRIENTS;
  case NutritionLabelCountry.US:
    return USA_PREVIEW_NUTRIENTS;
  case NutritionLabelCountry.UK:
    return UK_PREVIEW_NUTRIENTS;
  }
}

export const NUTRIENT_UNIT_MAP: Map<NutrientType, string> = new Map<NutrientType, string>([
  [NutrientType.Energy, "kcal"],
  [NutrientType.Fat, "g"],
  [NutrientType.SaturatedFattyAcids, "g"],
  [NutrientType.TransFattyAcids, "g"],
  [NutrientType.Cholesterol, "mg"],
  [NutrientType.Sodium, "mg"],
  [NutrientType.Carbohydrate, "g"],
  [NutrientType.Fibre, "g"],
  [NutrientType.Sugars, "g"],
  [NutrientType.AddedSugar, "g"],
  [NutrientType.Protein, "g"],
  [NutrientType.VitaminD, "mcg"],
  [NutrientType.Potassium, "mg"],
  [NutrientType.Calcium, "mg"],
  [NutrientType.Iron, "mg"],
  [NutrientType.SugarAlcohols, "g"],
  [NutrientType.VitaminC, "mg"],
  [NutrientType.VitaminA, "mg"],
  [NutrientType.MonoUnsaturatedFats, "g"],
  [NutrientType.PolyUnsaturatedFats, "g"],
  [NutrientType.Omega3, "g"],
  [NutrientType.Omega6, "g"],
  [NutrientType.Biotin, "mcg"],
  [NutrientType.Choline, "mg"],
  [NutrientType.Folate, "mcg"],
  // [NutrientType.FolateDfe, ""],
  [NutrientType.Niacin, "mg"],
  // [NutrientType.NiacinEq, ""],
  // [NutrientType.NiacinVitb3Eq, ""],
  [NutrientType.PantothenicAcid, "mg"],
  [NutrientType.Riboflavin, "mg"],
  [NutrientType.Thiamine, "mg"],
  [NutrientType.BetaCarotene, "mcg"],
  [NutrientType.Retinol, "mcg"],
  [NutrientType.VitaminB12, "mcg"], // (Vitamin D, another, ""])
  [NutrientType.VitaminE, "mg"],
  // [NutrientType.VitaminEToco, ""],
  // [NutrientType.VitaminETocoEq, ""],
  [NutrientType.VitaminK, "mcg"],
  [NutrientType.Chromium, "mcg"],
  [NutrientType.Chloride, "mg"],
  [NutrientType.Copper, "mg"],
  [NutrientType.Iodine, "mcg"],
  [NutrientType.Magnesium, "mg"],
  [NutrientType.Manganese, "mg"],
  [NutrientType.Molybdenum, "mcg"],
  [NutrientType.Phosphorus, "mg"],
  [NutrientType.Selenium, "mcg"],
  [NutrientType.Zinc, "mg"],
  [NutrientType.LAlanine, "mg"],
  [NutrientType.LArginine, "mg"],
  [NutrientType.LAsparagine, "mg"],
  [NutrientType.LAsparticAcid, "mg"],
  [NutrientType.LCysteine, "mg"],
  [NutrientType.LGlutamicAcid, "mg"],
  [NutrientType.LGlutamine, "mg"],
  [NutrientType.Glycine, "mg"],
  [NutrientType.LHistidine, "mg"],
  [NutrientType.LIsoleucine, "mg"],
  [NutrientType.LLeucine, "mg"],
  [NutrientType.LLysine, "mg"],
  [NutrientType.LMethionine, "mg"],
  [NutrientType.LPhenylalanine, "mg"],
  [NutrientType.LProline, "mg"],
  [NutrientType.LSerine, "mg"],
  [NutrientType.LThreonine, "mg"],
  [NutrientType.LTyrosine, "mg"],
  [NutrientType.LValine, "mg"],
  [NutrientType.Caffeine, "mg"],
  [NutrientType.GreenTeaExtract, ""],
  [NutrientType.Taurine, "mg"],
  [NutrientType.Ash, "g"],
  [NutrientType.Moisture, "mL"],
  [NutrientType.Phosphatidylserine, "mg"],
  [NutrientType.VitaminB6, "mg"],
  [NutrientType.FolicAcid, "mcg"],
  [NutrientType.Boron, "mcg"],
  [NutrientType.Fluoride, "mcg"],
  [NutrientType.Tryptophan, "g"],
]);
